import CloseIcon from '@mui/icons-material/Close';
import { Box, CircularProgress, IconButton, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { ApiWorkflowDialog, exportToCsv, Icon } from '@verantos-dev/shared-components';

import { FinalResultsContent } from './FinalResultsContent';
import { MappingResultsGrid } from './MappingResultsGrid';
import { useExportFinalResults } from '../hooks/useExportFinalResults';
import { useFinalResultsColumns } from '../hooks/useFinalResultsColumns';
import { useFlattenedFinalResults } from '../hooks/useFlattenedFinalResults';
import type { UseMappingReviewDialogReturn } from '../hooks/useMappingReviewDialog';
import type { MappingResponse } from '../types';

/**
 * CombinedMappingDialogContent Component
 *
 * Renders the combined dialog content with tabs for mapping results and final concept list.
 * This replaces the separate review and confirm steps with a single tabbed interface.
 */

interface CombinedMappingDialogContentProps {
  isLoading: boolean;
  error: string | null;
  mappingData: MappingResponse | null;
  flatRows: ReturnType<typeof useFlattenedFinalResults>;
  columns: ReturnType<typeof useFinalResultsColumns>;
  onExportMappingResultsCSV: () => void;
  onExportFinalConceptListCSV: () => void;
  onSaveAsConceptGroup: () => void;
  onClose: () => void;
}

const CombinedMappingDialogContent = ({
  isLoading,
  error,
  mappingData,
  flatRows,
  columns,
  onExportMappingResultsCSV,
  onExportFinalConceptListCSV,
  onClose,
}: CombinedMappingDialogContentProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Still loading the mapping result data - show spinner while waiting for API response
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary">
          {intl.formatMessage({ id: 'codeListMapper.loading.mappingResults' })}
        </Typography>
      </Box>
    );
  }

  // Error occurred during API call or data loading
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          gap: 2,
          p: 3,
        }}
      >
        <Typography variant="h6" color="error">
          {intl.formatMessage({ id: 'codeListMapper.error.failedToLoad' })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {intl.formatMessage({ id: 'codeListMapper.error.tryAgain' })}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            p: 1.5,
            backgroundColor: 'error.lighter',
            borderRadius: 1,
            fontFamily: 'monospace',
            maxWidth: '100%',
            wordBreak: 'break-word',
          }}
        >
          {error}
        </Typography>
      </Box>
    );
  }

  if (!mappingData) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          gap: 2,
        }}
      >
        <Typography variant="h6" color="error">
          {intl.formatMessage({ id: 'codeListMapper.error.failedToLoad' })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {intl.formatMessage({ id: 'codeListMapper.error.tryAgain' })}
        </Typography>
      </Box>
    );
  }

  const { results, fileName: mappingFileName } = mappingData;
  const displayFileName = mappingFileName.endsWith('.csv')
    ? mappingFileName
    : `${mappingFileName}.csv`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header with title and close button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pt: 3,
          pb: 2,
        }}
      >
        <Typography variant="h6">
          {intl.formatMessage(
            { id: 'codeListMapper.title.reviewMappingResults' },
            { fileName: displayFileName }
          )}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            borderRadius: '4px',
            '&:hover': {
              color: 'text.primary',
              backgroundColor: 'action.hover',
              borderRadius: '4px',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Tabs and download icon on the same row */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        {/* Tabs */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: theme.palette.grey[200],
            borderRadius: 1,
            p: 0.5,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 32,
              '& .MuiTab-root': {
                typography: 'body2',
                textTransform: 'none',
                fontWeight: 500,
                letterSpacing: '0.4px',
                minHeight: 32,
                px: 3,
                borderRadius: 0,
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.common.white,
                  color: theme.palette.text.primary,
                  boxShadow: theme.shadows[1],
                },
              },
              '& .MuiTabs-flexContainer': {
                gap: 0.5,
              },
              '& .MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            <Tab
              label={intl.formatMessage({ id: 'codeListMapper.section.mappingResults' })}
              disableRipple
            />
            <Tab
              label={intl.formatMessage({ id: 'codeListMapper.section.finalConceptList' })}
              disableRipple
            />
          </Tabs>
        </Box>

        {/* Download icon */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {activeTab === 0 && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: 0.65,
                color: 'action.active',
                transition: 'background-color 0.15s ease, opacity 0.15s ease',
                '&:hover': {
                  opacity: 1,
                  backgroundColor: 'action.hover',
                },
              }}
              onClick={onExportMappingResultsCSV}
            >
              <Icon id="export-mapping-results-csv" icon="download" />
            </Box>
          )}
          {activeTab === 1 && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: 0.65,
                color: 'action.active',
                transition: 'background-color 0.15s ease, opacity 0.15s ease',
                '&:hover': {
                  opacity: 1,
                  backgroundColor: 'action.hover',
                },
              }}
              onClick={onExportFinalConceptListCSV}
            >
              <Icon id="export-final-concept-list-csv" icon="download" />
            </Box>
          )}
        </Box>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {activeTab === 0 && (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              pb: 2,
            }}
          >
            {/* Results Table using DataGrid */}
            <Box sx={{ flex: 1 }}>
              <MappingResultsGrid results={results} />
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Final Results Content */}
            <Box sx={{ flex: 1 }}>
              <FinalResultsContent
                mappingData={mappingData}
                flatRows={flatRows}
                columns={columns}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * CombinedMappingDialog Component
 *
 * A single dialog that combines both mapping results and final concept list
 * into a tabbed interface, as shown in the attached images.
 */

type CombinedMappingDialogProps = Omit<UseMappingReviewDialogReturn, 'handleOpenDialog'>;

export const CombinedMappingDialog = ({
  isDialogOpen,
  mappingData,
  isLoading,
  error,
  handleCloseDialog,
}: CombinedMappingDialogProps) => {
  // ============================================================================
  // BUSINESS LOGIC (encapsulated in hook)
  // ============================================================================
  // Compute flattened rows and columns directly for the combined dialog
  const flatRows = useFlattenedFinalResults(mappingData?.results || []);
  const columns = useFinalResultsColumns();

  // Export hook for CSV download (final concept list - tab 1)
  const { handleExport } = useExportFinalResults({
    flatRows,
    columns,
  });

  // Export function for mapping results (tab 0)
  const handleExportMappingResultsCSV = useCallback(() => {
    if (!mappingData?.results || mappingData.results.length === 0) {
      console.warn('No data to export');
      return;
    }

    /**
     * Helper function to get sort order based on status
     * Primary sort by status: Not found (0), Unmapped (1), Mapped (2), Already standard (3)
     */
    const getStatusOrder = (status: string): number => {
      const statusLower = status.toLowerCase();
      if (statusLower === 'not found') return 0;
      if (statusLower === 'unmapped') return 1;
      if (
        statusLower === 'mapped' ||
        statusLower === 'one mapping' ||
        statusLower === 'multiple mappings' ||
        statusLower === 'multiple mapping'
      )
        return 2;
      if (statusLower === 'already standard') return 3;
      return 4; // Unknown status goes to end
    };

    // First sort the results in the same way as the display table
    const sortedResults = [...mappingData.results].sort((a, b) => {
      // First sort by status
      const statusOrderA = getStatusOrder(a.status || '');
      const statusOrderB = getStatusOrder(b.status || '');

      if (statusOrderA !== statusOrderB) {
        return statusOrderA - statusOrderB;
      }

      // If status is "Mapped" or similar, sort by number of nested mapped rows
      // Rows with multiple mapped rows (count > 1) should come before those with single mapped row (count === 1)
      if (statusOrderA === 2) {
        // Status is "Mapped"
        const countA = a.isCollapsible && a.mappedRows ? a.mappedRows.length : 0;
        const countB = b.isCollapsible && b.mappedRows ? b.mappedRows.length : 0;

        // Multiple mappings (count > 1) should come before single mappings (count === 1)
        if (countA > 1 && countB === 1) return -1;
        if (countA === 1 && countB > 1) return 1;

        // Within same count, sort by count descending
        return countB - countA;
      }

      return 0;
    });

    // Transform sorted rows for mapping results export
    const transformedRows = sortedResults.flatMap((result) => {
      const rows: any[] = [];

      // Add the Source row
      const sourceRow = {
        type: result.type,
        id: result.id,
        name: result.name,
        code: result.code,
        vocabulary: result.vocabulary,
        standard:
          result.type === 'Source' && result.status?.toLowerCase() === 'already standard'
            ? 'Yes'
            : result.type === 'Mapped'
            ? 'Yes'
            : 'No',
        domain: result.domain,
        class: result.class,
        status: result.status,
        mappedCount: result.mappedCount,
      };
      rows.push(sourceRow);

      // Add mapped rows if they exist, sorted alphabetically by name
      if (result.mappedRows && result.mappedRows.length > 0) {
        const sortedMappedRows = [...result.mappedRows].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        );

        sortedMappedRows.forEach((mappedRow) => {
          rows.push({
            ...mappedRow,
            standard: 'Yes',
            status: '', // Remove status for nested mapped rows
          });
        });
      }

      return rows;
    });

    // Define columns for mapping results export
    const csvColumns = [
      { field: 'type' as const, headerName: 'Type' },
      { field: 'id' as const, headerName: 'ID' },
      { field: 'name' as const, headerName: 'Name' },
      { field: 'code' as const, headerName: 'Code' },
      { field: 'vocabulary' as const, headerName: 'Vocabulary' },
      { field: 'standard' as const, headerName: 'Standard' },
      { field: 'domain' as const, headerName: 'Domain' },
      { field: 'class' as const, headerName: 'Class' },
      { field: 'status' as const, headerName: 'Status' },
      { field: 'mappedCount' as const, headerName: 'Mapped count' },
    ];

    // Generate filename
    const baseFileName = mappingData?.fileName?.replace(/\.csv$/, '') || undefined;
    const csvFilename = baseFileName
      ? `${baseFileName}_mapping_results.csv`
      : 'mapping-results.csv';

    // Trigger CSV download
    exportToCsv(csvFilename, transformedRows, csvColumns);
  }, [mappingData]);

  // Export function for final concept list (tab 1)
  const handleExportFinalConceptListCSV = useCallback(() => {
    const baseFileName = mappingData?.fileName?.replace(/\.csv$/, '') || undefined;
    const filename = baseFileName
      ? `${baseFileName}_final_concept_list.csv`
      : 'final_concept_list.csv';
    handleExport(filename);
  }, [handleExport, mappingData]);

  const handleExportCSV = () => {
    // This will be passed down and overridden per tab
    // For now, use the final concept list export as default
    handleExportFinalConceptListCSV();
  };

  const handleSaveAsConceptGroup = () => {
    // TODO: Implement save as concept group functionality
    // eslint-disable-next-line no-console
    console.log('Save as concept group clicked');
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  if (!isDialogOpen) {
    return null;
  }

  return (
    <ApiWorkflowDialog
      id="combined-mapping-dialog"
      title=""
      size="full"
      maxWidth={false}
      isValidForSubmit={false}
      onClose={handleCloseDialog}
      onSubmit={undefined}
      displayButtons={false}
      closeOnBackdropClick={false}
    >
      <CombinedMappingDialogContent
        isLoading={isLoading}
        error={error}
        mappingData={mappingData}
        flatRows={flatRows}
        columns={columns}
        onExportMappingResultsCSV={handleExportMappingResultsCSV}
        onExportFinalConceptListCSV={handleExportFinalConceptListCSV}
        onSaveAsConceptGroup={handleSaveAsConceptGroup}
        onClose={handleCloseDialog}
      />
    </ApiWorkflowDialog>
  );
};
